import React, { useState, useCallback, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { fetchOldAllMember } from "../Redux/Slice/AddMember";
import Loader from "../Components/Loader/Loader";

const InputFieldWithLabel = ({
  label,
  name,
  type = "text",
  placeholder,
  value,
  error,
  onChange,
  disabled = false,
}) => (
  <div className="col-span-12 md:col-span-6">
    <label className="block text-sm font-bold text-gray-900 mb-1">
      {label}
    </label>
    <input
      type={type}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-[#122A47] focus:border-[#122A47] ${disabled ? "bg-gray-100 cursor-not-allowed text-gray-500" : ""
        }`}
    />
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

const cityStateMap = {
  "Surat": "Gujarat",
  "Ahmedabad": "Gujarat",
  "Vadodara": "Gujarat",
  "Rajkot": "Gujarat",
  "Mumbai": "Maharashtra",
  "Pune": "Maharashtra",
  "Delhi": "Delhi",
  "Bangalore": "Karnataka",
  "Hyderabad": "Telangana",
  "Chennai": "Tamil Nadu"
};

const JobOfferLetter = () => {
  const dispatch = useDispatch();
  const { MemberOldAllData, MemberOldAllLoading } = useSelector(
    (state) => state.AddMemberSlice
  );

  const [selectedEmpId, setSelectedEmpId] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cityDropdownVal, setCityDropdownVal] = useState("");

  const [formValues, setFormValues] = useState({
    date: "",
    employeeName: "",
    employeeAddress: "",
    city: "",
    state: "",
    zipCode: "",
    jobTitle: "",
    companyName: "",
    departmentName: "",
    managerName: "",
    officeLocation: "Empire State Building, Maan Darwaja, Surat",
    joiningDate: "",
    salaryAmount: "",
    salaryDuration: "month",
    workingHours: "",
    confirmationDate: "",
  });

  const [errors, setErrors] = useState({});
  const [generatedText, setGeneratedText] = useState("");
  const pdfContentRef = useRef(null);

  // Fetch employees on mount
  useEffect(() => {
    dispatch(fetchOldAllMember());
  }, [dispatch]);

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getEmployeeSalary = (emp) => {
    if (!emp) return "";
    if (typeof emp.Salary === "string" || typeof emp.Salary === "number") {
      return emp.Salary;
    }
    if (Array.isArray(emp.Salary)) {
      let baseSalary = 0;
      let increment = 0;
      emp.Salary.forEach((item) => {
        if (!item || !item.Salary) return;
        const amount = Number(item.Salary);
        if (isNaN(amount)) return;
        if (item.Discription === "Salary") {
          baseSalary = amount;
        }
        if (item.Discription === "Increment") {
          increment += amount;
        }
      });
      return baseSalary + increment || "";
    }
    return "";
  };

  // Populate data when employee is selected
  const handleEmployeeSelect = (e) => {
    const empId = e.target.value;
    setSelectedEmpId(empId);
    setGeneratedText("");

    if (!empId) {
      resetForm();
      return;
    }

    const emp = MemberOldAllData?.data?.find((el) => el._id === empId);
    if (emp) {
      setFormValues({
        date: formatDateForInput(new Date()), // Default to today
        employeeName: emp.Name || "",
        employeeAddress: emp.Address || "",
        city: "",
        state: "",
        zipCode: "",
        jobTitle: emp.Role || "",
        companyName: emp.Firm || "",
        departmentName: "",
        managerName: "",
        officeLocation: "Empire State Building, Maan Darwaja, Surat",
        joiningDate: formatDateForInput(emp.JoiningDate) || "",
        salaryAmount: getEmployeeSalary(emp),
        salaryDuration: "month",
        workingHours: "Monday to Saturday, 10:00 AM to 7:00 PM",
        confirmationDate: "",
      });
      setCityDropdownVal("");
    }
  };

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));

    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      if (newErrors[name]) delete newErrors[name];
      return newErrors;
    });
  }, []);

  const validateForm = () => {
    const requiredFields = [
      "date",
      "employeeName",
      "employeeAddress",
      "city",
      "state",
      "zipCode",
      "jobTitle",
      "companyName",
      "departmentName",
      "managerName",
      "officeLocation",
      "joiningDate",
      "salaryAmount",
      "workingHours",
      "confirmationDate",
    ];

    const newErrors = {};
    requiredFields.forEach((field) => {
      if (!formValues[field]) newErrors[field] = "This field is required";
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const generateTemplateText = (data) => {
    const formattedDate = formatDate(data.date);
    const formattedJoiningDate = formatDate(data.joiningDate);
    const formattedConfirmationDate = formatDate(data.confirmationDate);

    return `
<div style="font-family: Arial, sans-serif; font-size: 13px; line-height: 1.2rem; padding: 10px; margin-top: 60px;">
  <p><strong>Date:</strong> ${formattedDate}</p>

  <p>
    <strong>${data.employeeName}</strong><br/>
    ${data.employeeAddress}<br/>
    ${data.city}, ${data.state} - ${data.zipCode}
  </p>

  <p style="font-weight: bold; margin-top: 20px;">
    Subject: Job Joining Letter for the Position of ${data.jobTitle}
  </p>

  <p style="margin-top : 10px;">Dear <strong>${data.employeeName}</strong>,</p>

  <p style="text-align: justify; margin-top : 10px;">
    We are pleased to confirm your selection for the position of 
    <strong>${data.jobTitle}</strong> with <strong>${data.companyName}</strong>. 
    We are excited to welcome you to our team and look forward to your contributions 
    to our software development efforts.
  </p>

  <p style="text-align: justify; margin-top : 10px; margin-bottom : 10px;">Below are the details of your joining:</p>

  <p style="margin-left: 20px;">
    1. Designation: <strong>${data.jobTitle}</strong><br/>
    2. Department: <strong>${data.departmentName}</strong><br/>
    3. Reporting Manager: <strong>${data.managerName}</strong><br/>
    4. Job Location: <strong>${data.officeLocation}</strong><br/>
    5. Joining Date: <strong>${formattedJoiningDate}</strong><br/>
    6. Compensation Details:<br/>
    &nbsp;&nbsp;&nbsp;&nbsp;• Salary: <strong>₹${data.salaryAmount}</strong> per <strong>${data.salaryDuration}</strong><br/>
    7. Working Hours: <strong>${data.workingHours}</strong>
  </p>

  <p style="text-align: justify; margin-top: 20px;">
    Your joining date is <strong>${formattedJoiningDate}</strong> and you are requested 
    to report to <strong>${data.managerName}</strong>.
  </p>

  <p style="text-align: justify; margin-top: 10px;">
    We are confident that you will thrive in our dynamic environment and contribute to 
    our projects with innovation and enthusiasm.
  </p>

  <p style="text-align: justify; margin-top: 10px;">
    Please confirm your acceptance of this offer by signing the enclosed copy of this 
    letter and returning it to us by <strong>${formattedConfirmationDate}</strong>.
  </p>

  <p style="text-align: justify; margin-top: 10px;">
    We look forward to your joining and wish you success at 
    <strong>${data.companyName}</strong>.
  </p>

  <p style="margin-top: 40px;">Best regards,</p>
  <p style="margin-top: 20px; font-weight: bold;">Dipak Rajput</p>
  <p>Project Manager</p>

  <h4 style="margin-top: 20px; font-weight: bold;">Acknowledgment by Employee</h4>
  <p style="text-align: justify;">
    I, <strong>${data.employeeName}</strong>, accept the terms and conditions mentioned 
    in this job joining letter.
  </p>

  <p>Signature: ________________________</p>
  <p>Date: _____________________________</p>
</div>
    `;
  };

  const handleModalSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const text = generateTemplateText(formValues);
      setGeneratedText(text);
      setErrors({});
      setIsModalOpen(false);
    }
  };

  const resetForm = () => {
    setFormValues({
      date: "",
      employeeName: "",
      employeeAddress: "",
      city: "",
      state: "",
      zipCode: "",
      jobTitle: "",
      companyName: "",
      departmentName: "",
      managerName: "",
      officeLocation: "Empire State Building, Maan Darwaja, Surat",
      joiningDate: "",
      salaryAmount: "",
      salaryDuration: "month",
      workingHours: "",
      confirmationDate: "",
    });
    setErrors({});
    setGeneratedText("");
    setSelectedEmpId("");
    setCityDropdownVal("");
  };

  const handleDownloadPdf = async () => {
    const element = pdfContentRef.current;
    if (!element) return;

    const pdf = new jsPDF("p", "mm", "a4");
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });
    const imgData = canvas.toDataURL("image/png");
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save(`Job_Offer_${formValues.employeeName || "Employee"}.pdf`);
  };

  if (MemberOldAllLoading) return <Loader />;

  return (
    <div className="min-h-screen p-4 lg:p-8 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-xl font-bold bg-[#122A47] text-white px-4 py-2 inline-block rounded mb-6">
          JOB JOINING OFFER LETTER
        </h1>

        {/* STEP 1: Employee Selection Card */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#122A47] mb-4">
            Step 1: Select Employee
          </h2>
          <div className="grid grid-cols-12 gap-4 items-center">
            <div className="col-span-12 md:col-span-8">
              <select
                value={selectedEmpId}
                onChange={handleEmployeeSelect}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-[#122A47] focus:border-[#122A47] bg-white text-gray-900 font-medium"
              >
                <option value="">-- Choose Employee / Intern --</option>
                {MemberOldAllData?.data?.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.Name} {emp.Role ? `(${emp.Role})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-12 md:col-span-4 flex gap-2">
              <button
                disabled={!selectedEmpId}
                onClick={() => setIsModalOpen(true)}
                className={`flex-1 text-white py-2 px-4 rounded-md transition-colors font-semibold ${selectedEmpId
                  ? "bg-[#122A47] hover:bg-[#0c2545]"
                  : "bg-gray-400 cursor-not-allowed"
                  }`}
              >
                Fill Details & Open Template
              </button>
              <button
                onClick={resetForm}
                className="bg-gray-500 text-white py-2 px-4 rounded-md hover:bg-gray-600 transition-colors font-semibold"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* STEP 2: The Modal (Popup Form) */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-[#122A47] text-white rounded-t-lg">
                <h3 className="text-lg font-bold">Edit Job Offer Details</h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-white hover:text-gray-300 text-2xl font-bold"
                >
                  &times;
                </button>
              </div>

              <form onSubmit={handleModalSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-12 gap-4">
                  <InputFieldWithLabel
                    label="Letter Date"
                    name="date"
                    type="date"
                    value={formValues.date}
                    error={errors.date}
                    onChange={handleChange}
                  />

                  <InputFieldWithLabel
                    label="Employee Name (from Database)"
                    name="employeeName"
                    value={formValues.employeeName}
                    error={errors.employeeName}
                    onChange={handleChange}
                  />

                  <div className="col-span-12 md:col-span-6">
                    <label className="block text-sm font-bold text-gray-900 mb-1">
                      Company Name
                    </label>
                    <select
                      name="companyName"
                      value={formValues.companyName}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-[#122A47] focus:border-[#122A47] text-gray-900"
                    >
                      <option value="">-- Select Company --</option>
                      <option value="Dynamic Software">Dynamic Software</option>
                      <option value="Dynamic Softlink Pvt Ltd.">Dynamic Softlink Pvt Ltd.</option>
                      {formValues.companyName &&
                        formValues.companyName !== "Dynamic Software" &&
                        formValues.companyName !== "Dynamic Softlink Pvt Ltd." && (
                          <option value={formValues.companyName}>
                            {formValues.companyName} (from DB)
                          </option>
                        )}
                    </select>
                    {errors.companyName && (
                      <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>
                    )}
                  </div>

                  <div className="col-span-12 md:col-span-6">
                    <label className="block text-sm font-bold text-gray-900 mb-1">
                      Job Designation / Title
                    </label>
                    <select
                      name="jobTitle"
                      value={formValues.jobTitle}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-[#122A47] focus:border-[#122A47] text-gray-900"
                    >
                      <option value="">-- Select Title --</option>
                      <option value="Delphi Developer">Delphi Developer</option>
                      <option value="Web Developer">Web Developer</option>
                      {formValues.jobTitle &&
                        formValues.jobTitle !== "Delphi Developer" &&
                        formValues.jobTitle !== "Web Developer" && (
                          <option value={formValues.jobTitle}>
                            {formValues.jobTitle} (from DB)
                          </option>
                        )}
                    </select>
                    {errors.jobTitle && (
                      <p className="text-red-500 text-sm mt-1">{errors.jobTitle}</p>
                    )}
                  </div>

                  <InputFieldWithLabel
                    label="Joining Date (from Database)"
                    name="joiningDate"
                    type="date"
                    value={formValues.joiningDate}
                    error={errors.joiningDate}
                    onChange={handleChange}
                  />

                  <InputFieldWithLabel
                    label="Salary Amount (₹)"
                    name="salaryAmount"
                    value={formValues.salaryAmount}
                    error={errors.salaryAmount}
                    onChange={handleChange}
                  />

                  <div className="col-span-12 md:col-span-6">
                    <label className="block text-sm font-bold text-gray-900 mb-1">
                      Salary Duration
                    </label>
                    <select
                      name="salaryDuration"
                      value={formValues.salaryDuration}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-[#122A47] focus:border-[#122A47]"
                    >
                      <option value="month">Month</option>
                      <option value="year">Year</option>
                    </select>
                  </div>

                  <InputFieldWithLabel
                    label="Street Address (from Database)"
                    name="employeeAddress"
                    value={formValues.employeeAddress}
                    error={errors.employeeAddress}
                    onChange={handleChange}
                  />

                  <div className="col-span-12 md:col-span-6">
                    <label className="block text-sm font-bold text-gray-900 mb-1">
                      Select City
                    </label>
                    <select
                      value={cityDropdownVal}
                      onChange={(e) => {
                        const val = e.target.value;
                        setCityDropdownVal(val);
                        if (val && val !== "Other") {
                          setFormValues((prev) => ({
                            ...prev,
                            city: val,
                            state: cityStateMap[val] || "",
                          }));
                        } else {
                          setFormValues((prev) => ({
                            ...prev,
                            city: "",
                            state: "",
                          }));
                        }
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-[#122A47] focus:border-[#122A47] text-gray-900"
                    >
                      <option value="">-- Select City --</option>
                      {Object.keys(cityStateMap).map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                      <option value="Other">Other / Custom</option>
                    </select>
                  </div>

                  {cityDropdownVal && cityDropdownVal !== "Other" && (
                    <InputFieldWithLabel
                      label="State (Auto-filled)"
                      name="state"
                      value={formValues.state}
                      onChange={() => { }}
                      disabled={true}
                    />
                  )}

                  {cityDropdownVal === "Other" && (
                    <>
                      <InputFieldWithLabel
                        label="City"
                        name="city"
                        placeholder="Enter City Name"
                        value={formValues.city}
                        error={errors.city}
                        onChange={handleChange}
                      />
                      <InputFieldWithLabel
                        label="State"
                        name="state"
                        placeholder="Enter State Name"
                        value={formValues.state}
                        error={errors.state}
                        onChange={handleChange}
                      />
                    </>
                  )}

                  <InputFieldWithLabel
                    label="Zip Code"
                    name="zipCode"
                    placeholder="e.g., 395001"
                    value={formValues.zipCode}
                    error={errors.zipCode}
                    onChange={handleChange}
                  />

                  <div className="col-span-12 md:col-span-6">
                    <label className="block text-sm font-bold text-gray-900 mb-1">
                      Department Name
                    </label>
                    <select
                      name="departmentName"
                      value={formValues.departmentName}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-[#122A47] focus:border-[#122A47] text-gray-900"
                    >
                      <option value="">-- Select Department --</option>
                      <option value="Web Development Department">
                        Web Development Department
                      </option>
                      <option value="Delphi Development Department">
                        Delphi Development Department
                      </option>
                      {formValues.departmentName &&
                        formValues.departmentName !== "Web Development Department" &&
                        formValues.departmentName !== "Delphi Development Department" && (
                          <option value={formValues.departmentName}>
                            {formValues.departmentName} (Custom)
                          </option>
                        )}
                    </select>
                    {errors.departmentName && (
                      <p className="text-red-500 text-sm mt-1">{errors.departmentName}</p>
                    )}
                  </div>

                  <div className="col-span-12 md:col-span-6">
                    <label className="block text-sm font-bold text-gray-900 mb-1">
                      Reporting Manager
                    </label>
                    <select
                      name="managerName"
                      value={formValues.managerName}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-[#122A47] focus:border-[#122A47] text-gray-900"
                    >
                      <option value="">-- Select Manager --</option>
                      <option value="JAY PARMAR">JAY PARMAR</option>
                      <option value="DIPAK RAJPUT">DIPAK RAJPUT</option>
                      {formValues.managerName &&
                        formValues.managerName !== "JAY PARMAR" &&
                        formValues.managerName !== "DIPAK RAJPUT" && (
                          <option value={formValues.managerName}>
                            {formValues.managerName} (Custom)
                          </option>
                        )}
                    </select>
                    {errors.managerName && (
                      <p className="text-red-500 text-sm mt-1">{errors.managerName}</p>
                    )}
                  </div>

                  <InputFieldWithLabel
                    label="Office Location"
                    name="officeLocation"
                    placeholder="e.g., Head Office, Surat"
                    value={formValues.officeLocation}
                    error={errors.officeLocation}
                    onChange={handleChange}
                  />

                  <div className="col-span-12 md:col-span-6">
                    <label className="block text-sm font-bold text-gray-900 mb-1">
                      Working Days & Hours
                    </label>
                    <select
                      name="workingHours"
                      value={formValues.workingHours}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:ring-[#122A47] focus:border-[#122A47] text-gray-900"
                    >
                      <option value="">-- Select Work Hours --</option>
                      <option value="Monday to Saturday, 10:00 AM to 7:00 PM">
                        Monday to Saturday, 10:00 AM to 7:00 PM
                      </option>
                      <option value="Monday to Saturday, 10:00 AM to 6:00 PM">
                        Monday to Saturday, 10:00 AM to 6:00 PM
                      </option>
                      {formValues.workingHours &&
                        formValues.workingHours !== "Monday to Saturday, 10:00 AM to 7:00 PM" &&
                        formValues.workingHours !== "Monday to Saturday, 10:00 AM to 6:00 PM" && (
                          <option value={formValues.workingHours}>
                            {formValues.workingHours} (Custom)
                          </option>
                        )}
                    </select>
                    {errors.workingHours && (
                      <p className="text-red-500 text-sm mt-1">{errors.workingHours}</p>
                    )}
                  </div>

                  <InputFieldWithLabel
                    label="Acceptance Return Date"
                    name="confirmationDate"
                    type="date"
                    value={formValues.confirmationDate}
                    error={errors.confirmationDate}
                    onChange={handleChange}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 mt-6">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="bg-gray-500 text-white py-2 px-4 rounded-md hover:bg-gray-600 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-[#122A47] text-white py-2 px-4 rounded-md hover:bg-[#0c2545] transition-colors font-medium"
                  >
                    Generate Document
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* STEP 3: Preview and Download Panel */}
        {generatedText && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-md p-6 mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-[#122A47]">
                Letter Preview
              </h2>
              <button
                onClick={handleDownloadPdf}
                className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors font-semibold"
              >
                Download PDF
              </button>
            </div>

            <div className="max-w-full overflow-x-auto bg-gray-100 p-4 rounded-lg border border-gray-300">
              <div
                ref={pdfContentRef}
                className="bg-white shadow-lg mx-auto"
                style={{
                  width: "180mm",
                  minHeight: "270mm",
                  padding: "15mm",
                  boxSizing: "border-box",
                }}
              >
                <div
                  className="font-sans text-[13px] text-left leading-relaxed text-gray-900"
                  dangerouslySetInnerHTML={{ __html: generatedText }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobOfferLetter;
